/**
 * The frame every platform-admin screen sits in (owner, 1 Oct 2026: "happy with the colour, but the
 * pastel infills look old and tired").
 *
 * The look: a deep navy header band — the same navy as the partner portal's sidebar — carrying the
 * title, actions and tabs; white working surfaces with hairline borders below; colour used for
 * meaning only (solid primary actions, a coloured dot for status, a solid icon on a message), never as
 * a tinted fill behind content.
 */
import React from 'react';
import { ArrowLeft, AlertTriangle, CheckCircle2, Info } from 'lucide-react';

export const ADMIN_NAVY = '#0b1a33';
export const ADMIN_BLUE = '#1a6fd4';

export interface AdminTab<K extends string> {
  key: K;
  label: string;
}

interface AdminShellProps<K extends string> {
  title: string;
  subtitle?: React.ReactNode;
  icon: React.ReactNode;
  onBack: () => void;
  /** What the top-left link says. "Back" by default; "Sign out" where this screen is home. */
  backLabel?: string;
  actions?: React.ReactNode;
  tabs?: readonly AdminTab<K>[];
  activeTab?: K;
  onTab?: (key: K) => void;
  children: React.ReactNode;
}

export function AdminShell<K extends string>({ title, subtitle, icon, onBack, backLabel = 'Back', actions, tabs, activeTab, onTab, children }: AdminShellProps<K>) {
  return (
    <div className="min-h-screen bg-white" style={{ fontFamily: "'DM Sans', system-ui, sans-serif" }}>
      <header style={{ background: ADMIN_NAVY }} className="text-white">
        <div className="mx-auto max-w-6xl px-4 pt-5 sm:px-8">
          <button type="button" onClick={onBack} className="mb-5 inline-flex items-center gap-1.5 text-sm text-white/60 transition hover:text-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#17b8e0] rounded">
            <ArrowLeft size={15} /> {backLabel}
          </button>
          <div className="flex flex-wrap items-end gap-4 pb-5">
            <div className="flex items-center gap-3.5">
              <div className="flex h-11 w-11 items-center justify-center rounded-xl text-white" style={{ background: `linear-gradient(135deg, ${ADMIN_BLUE}, #17b8e0)` }}>
                {icon}
              </div>
              <div>
                <h1 className="text-2xl font-semibold tracking-tight">{title}</h1>
                {subtitle && <p className="mt-0.5 text-sm text-white/60">{subtitle}</p>}
              </div>
            </div>
            {actions && <div className="ml-auto flex flex-wrap items-center gap-2">{actions}</div>}
          </div>
          {tabs && tabs.length > 0 && (
            <nav className="-mb-px flex gap-6" role="tablist">
              {tabs.map((t) => (
                <button
                  key={t.key}
                  type="button"
                  role="tab"
                  aria-selected={activeTab === t.key}
                  onClick={() => onTab?.(t.key)}
                  className={`border-b-2 pb-3 text-sm font-semibold transition focus-visible:outline-none ${
                    activeTab === t.key ? 'border-[#17b8e0] text-white' : 'border-transparent text-white/55 hover:text-white'
                  }`}
                >
                  {t.label}
                </button>
              ))}
            </nav>
          )}
        </div>
      </header>
      <main className="mx-auto max-w-6xl px-4 py-7 sm:px-8">{children}</main>
    </div>
  );
}

/** A button on the navy header. */
export const HeaderButton = ({ onClick, children }: { onClick: () => void; children: React.ReactNode }) => (
  <button
    type="button"
    onClick={onClick}
    className="inline-flex items-center gap-1.5 rounded-lg border border-white/15 px-3 py-2 text-sm font-medium text-white/85 transition hover:border-white/35 hover:text-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#17b8e0]"
  >
    {children}
  </button>
);

/** A message: white surface, the meaning carried by a solid icon and the border colour, no tint. */
export const AdminMessage = ({ tone, children }: { tone: 'error' | 'success' | 'info'; children: React.ReactNode }) => {
  const style = {
    error: { ring: 'border-red-300', dot: 'bg-red-600', Icon: AlertTriangle },
    success: { ring: 'border-emerald-300', dot: 'bg-emerald-600', Icon: CheckCircle2 },
    info: { ring: 'border-slate-200', dot: 'bg-[#1a6fd4]', Icon: Info },
  }[tone];
  const { Icon } = style;
  return (
    <div className={`mb-5 flex items-start gap-3 rounded-xl border bg-white px-4 py-3 ${style.ring}`} role={tone === 'error' ? 'alert' : 'status'}>
      <span className={`mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-full text-white ${style.dot}`}>
        <Icon size={14} />
      </span>
      <div className="text-sm leading-relaxed text-slate-700">{children}</div>
    </div>
  );
};

/** Status as a coloured dot and a word. */
export const StatusDot = ({ color, label }: { color: string; label: string }) => (
  <span className="inline-flex items-center gap-1.5 text-sm font-medium text-slate-700">
    <span className="h-2 w-2 rounded-full" style={{ background: color }} aria-hidden />
    {label}
  </span>
);

/** Small uppercase heading for a section of a page. */
export const SectionLabel = ({ children }: { children: React.ReactNode }) => (
  <h2 className="mb-3 text-xs font-semibold uppercase tracking-[0.08em] text-slate-500">{children}</h2>
);
